import React, { useState, useEffect, useCallback, useRef } from 'react';
import { X, ChevronRight, ChevronLeft, SkipForward } from 'lucide-react';
import { Button } from './ui/button';

const API = process.env.REACT_APP_BACKEND_URL;

// ── Spotlight overlay that cuts a hole around the target ──
const Spotlight = ({ targetRect, onClick }) => {
  if (!targetRect) {
    return <div className="fixed inset-0 z-[9998] bg-black/50 transition-opacity duration-300" onClick={onClick} />;
  }
  const pad = 8;
  const r = 12;
  const { top, left, width, height } = targetRect;
  const clipPath = `polygon(
    0% 0%, 0% 100%, 100% 100%, 100% 0%, 0% 0%,
    ${left - pad}px ${top - pad}px,
    ${left - pad}px ${top + height + pad}px,
    ${left + width + pad}px ${top + height + pad}px,
    ${left + width + pad}px ${top - pad}px,
    ${left - pad}px ${top - pad}px
  )`;
  return (
    <>
      <div className="fixed inset-0 z-[9998] bg-black/50 transition-opacity duration-300" style={{ clipPath }} onClick={onClick} />
      <div
        className="fixed z-[9998] pointer-events-none border-2 border-amber-400 transition-all duration-300"
        style={{
          top: top - pad, left: left - pad,
          width: width + pad * 2, height: height + pad * 2,
          borderRadius: r, boxShadow: '0 0 0 4px rgba(217,119,6,0.2)',
        }}
      />
    </>
  );
};

// ── Tooltip positioning ──
const getTooltipStyle = (targetRect, position, windowW, windowH) => {
  if (!targetRect || position === 'center') {
    return { position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)' };
  }
  const pad = 16;
  const { top, left, width, height } = targetRect;
  const cx = left + width / 2;
  const cy = top + height / 2;

  switch (position) {
    case 'bottom':
      return { position: 'fixed', top: top + height + pad + 8, left: Math.max(pad, Math.min(cx - 180, windowW - 380)), maxWidth: 360 };
    case 'top':
      return { position: 'fixed', bottom: windowH - top + pad + 8, left: Math.max(pad, Math.min(cx - 180, windowW - 380)), maxWidth: 360 };
    case 'right':
      return { position: 'fixed', top: Math.max(pad, cy - 60), left: left + width + pad + 8, maxWidth: 340 };
    case 'left':
      return { position: 'fixed', top: Math.max(pad, cy - 60), right: windowW - left + pad + 8, maxWidth: 340 };
    default:
      return { position: 'fixed', top: top + height + pad + 8, left: Math.max(pad, cx - 180), maxWidth: 360 };
  }
};

// ── Tour Engine ──
const TourEngine = ({ tour, onComplete, onSkip }) => {
  const [stepIdx, setStepIdx] = useState(0);
  const [targetRect, setTargetRect] = useState(null);
  const [windowSize, setWindowSize] = useState({ w: window.innerWidth, h: window.innerHeight });
  const tooltipRef = useRef(null);

  const steps = (tour?.steps || []).sort((a, b) => a.order - b.order);
  const step = steps[stepIdx];
  const isFirst = stepIdx === 0;
  const isLast = stepIdx === steps.length - 1;
  const token = localStorage.getItem('token');

  // Track window resize
  useEffect(() => {
    const onResize = () => setWindowSize({ w: window.innerWidth, h: window.innerHeight });
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Find and scroll to target element
  useEffect(() => {
    if (!step) return;
    const selector = step.target_selector;
    if (!selector) { setTargetRect(null); return; }

    // Try multiple selector strategies
    const findTarget = () => {
      let el = null;
      try {
        // Try CSS selector first
        el = document.querySelector(selector);
      } catch { }
      if (!el && selector.includes('has-text')) {
        // Parse has-text pseudo and find by text content
        const match = selector.match(/has-text\(['"](.+?)['"]\)/);
        if (match) {
          const text = match[1];
          const tag = selector.split(':')[0] || '*';
          const els = document.querySelectorAll(tag);
          for (const e of els) {
            if (e.textContent?.includes(text)) { el = e; break; }
          }
        }
      }
      return el;
    };

    const el = findTarget();
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      setTimeout(() => {
        const rect = el.getBoundingClientRect();
        setTargetRect({ top: rect.top, left: rect.left, width: rect.width, height: rect.height });
      }, 400);
    } else {
      setTargetRect(null);
    }
  }, [stepIdx, step]);

  // Save progress
  const saveProgress = useCallback(async (completed = false, skipped = false) => {
    if (!tour?.id || !token) return;
    try {
      await fetch(`${API}/api/tours/${tour.id}/progress`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ current_step: stepIdx, completed, skipped }),
      });
    } catch { }
  }, [tour?.id, stepIdx, token]);

  const handleNext = () => {
    if (isLast) {
      saveProgress(true);
      onComplete?.();
    } else {
      setStepIdx(s => s + 1);
      saveProgress();
    }
  };

  const handleBack = () => {
    if (!isFirst) setStepIdx(s => s - 1);
  };

  const handleSkip = () => {
    saveProgress(false, true);
    onSkip?.();
  };

  if (!step) return null;

  const tooltipStyle = getTooltipStyle(targetRect, step.position, windowSize.w, windowSize.h);
  const isCard = step.style === 'card' || step.position === 'center';
  const isBold = step.style === 'bold';

  return (
    <div className="tour-engine" data-testid="tour-engine">
      <Spotlight targetRect={targetRect} onClick={handleSkip} />

      {/* Tooltip */}
      <div
        ref={tooltipRef}
        className={`fixed z-[9999] transition-all duration-300 ${
          isCard
            ? 'bg-white rounded-2xl shadow-2xl border border-amber-200 p-6 sm:p-8 w-[90vw] max-w-md'
            : isBold
              ? 'bg-amber-600 text-white rounded-xl shadow-xl p-4 sm:p-5 max-w-sm'
              : 'bg-white rounded-xl shadow-xl border border-stone-200 p-4 sm:p-5 max-w-sm'
        }`}
        style={tooltipStyle}
        data-testid="tour-tooltip"
      >
        {/* Arrow indicator for non-center tooltips */}
        {!isCard && targetRect && step.position === 'bottom' && (
          <div className={`absolute -top-2 left-8 w-4 h-4 rotate-45 ${isBold ? 'bg-amber-600' : 'bg-white border-l border-t border-stone-200'}`} />
        )}

        {/* Close button */}
        <button
          onClick={handleSkip}
          className={`absolute top-3 right-3 p-1 rounded-full transition-colors ${
            isBold ? 'hover:bg-amber-700 text-amber-200' : 'hover:bg-stone-100 text-stone-400'
          }`}
          data-testid="tour-close"
        >
          <X className="h-4 w-4" />
        </button>

        {/* Card-style welcome illustration */}
        {isCard && isFirst && (
          <div className="flex justify-center mb-4">
            <div className="w-16 h-16 bg-amber-100 rounded-2xl flex items-center justify-center text-3xl">
              🐝
            </div>
          </div>
        )}
        {isCard && isLast && (
          <div className="flex justify-center mb-4">
            <div className="w-16 h-16 bg-green-100 rounded-2xl flex items-center justify-center text-3xl">
              ✅
            </div>
          </div>
        )}

        {/* Title */}
        <h3 className={`font-bold mb-2 ${
          isCard ? 'text-xl text-center text-stone-900' : isBold ? 'text-base text-white' : 'text-base text-stone-900'
        }`}>
          {step.title}
        </h3>

        {/* Content */}
        <div className={`text-sm leading-relaxed ${
          isCard ? 'text-center text-stone-600 mb-6' : isBold ? 'text-amber-100 mb-4' : 'text-stone-600 mb-4'
        }`} dangerouslySetInnerHTML={{ __html: step.content }} />

        {/* Progress + Navigation */}
        <div className={`flex items-center ${isCard ? 'justify-center' : 'justify-between'} gap-3`}>
          {/* Step dots */}
          <div className="flex items-center gap-1.5">
            {steps.map((_, i) => (
              <div
                key={i}
                className={`h-1.5 rounded-full transition-all ${
                  i === stepIdx
                    ? `w-5 ${isBold ? 'bg-white' : 'bg-amber-500'}`
                    : i < stepIdx
                      ? `w-1.5 ${isBold ? 'bg-amber-300' : 'bg-amber-300'}`
                      : `w-1.5 ${isBold ? 'bg-amber-800' : 'bg-stone-200'}`
                }`}
              />
            ))}
          </div>

          <div className="flex items-center gap-2">
            {!isFirst && (
              <Button
                size="sm" variant="ghost"
                onClick={handleBack}
                className={`h-8 text-xs ${isBold ? 'text-amber-200 hover:text-white hover:bg-amber-700' : 'text-stone-500'}`}
                data-testid="tour-back"
              >
                <ChevronLeft className="h-3.5 w-3.5 mr-0.5" /> Back
              </Button>
            )}
            {isFirst && !isLast && (
              <Button
                size="sm" variant="ghost"
                onClick={handleSkip}
                className={`h-8 text-xs ${isBold ? 'text-amber-200' : 'text-stone-400'}`}
                data-testid="tour-skip"
              >
                Skip Tour <SkipForward className="h-3.5 w-3.5 ml-0.5" />
              </Button>
            )}
            <Button
              size="sm"
              onClick={handleNext}
              className={`h-8 text-xs font-semibold ${
                isBold
                  ? 'bg-white text-amber-700 hover:bg-amber-50'
                  : 'bg-amber-600 hover:bg-amber-700 text-white'
              }`}
              data-testid="tour-next"
            >
              {isLast ? 'Done' : 'Next'} {!isLast && <ChevronRight className="h-3.5 w-3.5 ml-0.5" />}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TourEngine;
